import { defineStore } from 'pinia'

interface Atividade {
  id: string
  tipo: string
  acao: string
  objeto: string
  usuario: string
  usuario_nome: string
  descricao: string
  data: string
  ip: string | null
  metadados: any
  lida?: boolean
}

interface AtividadeResumo {
  total: number
  periodo: number
  ultimas_atividades: Atividade[]
}

interface AtividadesPaginadas {
  data: Atividade[]
  pagination: {
    total: number
    per_page: number
    current_page: number
    last_page: number
    from: number
    to: number
    has_more: boolean
  }
}

interface AtividadeResumoUsuario {
  total: number
  total_nao_lidas: number
  periodo: number
  ultimas_atividades: Atividade[]
}

interface AtividadesState {
  atividades: Atividade[]
  atividadesPaginadas: AtividadesPaginadas | null
  resumo: AtividadeResumo | null
  resumoUsuario: AtividadeResumoUsuario | null
  loading: boolean
  loadingPaginadas: boolean
  paginaAtual: number
  filtros: {
    periodo: string
    tipo: string
    busca: string
  }
}

export const useAtividadesStore = defineStore('atividades', {
  state: (): AtividadesState => ({
    atividades: [],
    atividadesPaginadas: null,
    resumo: null,
    resumoUsuario: null,
    loading: false,
    loadingPaginadas: false,
    paginaAtual: 1,
    filtros: {
      periodo: '30',
      tipo: '',
      busca: '',
    },
  }),

  getters: {
    atividadesFiltradas: (state) => {
      let resultado = state.atividades

      if (state.filtros.tipo) {
        resultado = resultado.filter(atividade => atividade.tipo === state.filtros.tipo)
      }

      if (state.filtros.busca) {
        const busca = state.filtros.busca.toLowerCase()
        resultado = resultado.filter(atividade =>
          atividade.descricao.toLowerCase().includes(busca) ||
          atividade.usuario_nome.toLowerCase().includes(busca) ||
          atividade.objeto.toLowerCase().includes(busca)
        )
      }

      return resultado
    },

    // Getters para sistema legado (compatibilidade)
    totalAtividades: state => state.resumo?.total || 0,
    ultimasAtividades: state => state.resumo?.ultimas_atividades || [],
    totalNaoLidas: state => state.resumo?.ultimas_atividades.filter(a => !a.lida).length || 0,

    // Getters para sistema por usuário específico
    totalAtividadesUsuario: state => state.resumoUsuario?.total || 0,
    ultimasAtividadesUsuario: state => state.resumoUsuario?.ultimas_atividades || [],
    totalNaoLidasUsuario: state => state.resumoUsuario?.total_nao_lidas || 0,

    // Getters para paginação
    atividadesPaginadasData: state => state.atividadesPaginadas?.data || [],
    paginationInfo: state => state.atividadesPaginadas?.pagination || null,
    hasMorePages: state => state.atividadesPaginadas?.pagination.has_more || false,
  },

  actions: {
    stripHtml (html: string): string {
      if (!html) { return '' }
      // Remove tags HTML e decodifica entidades HTML
      const div = document.createElement('div')
      div.innerHTML = html
      const text = div.textContent || div.innerText || ''
      return text.trim()
    },

    async carregarAtividades () {
      try {
        this.loading = true
        const apiStore = useApiStore()
        const response = await apiStore.request(`/gerencia/atividades?periodo=${this.filtros.periodo}&limite=100&pagina=1`) as { success: boolean; data: Atividade[] }

        console.log('carregarAtividades response:', response)

        // Normalizar os dados das atividades e remover HTML
        this.atividades = (response.data || []).map(atividade => ({
          ...atividade,
          descricao: this.stripHtml(atividade.descricao),
        }))
      } catch (error) {
        console.error('Erro ao carregar atividades:', error)
        this.atividades = []
      } finally {
        this.loading = false
      }
    },

    async carregarResumo () {
      try {
        const apiStore = useApiStore()
        const response = await apiStore.request('/gerencia/atividades/resumo?periodo=1', {}, 'financeiro', false) as any

        console.log('[AtividadesStore] carregarResumo - Response:', response)
        console.log('[AtividadesStore] carregarResumo - total_nao_lidas:', response?.total_nao_lidas)

        // Normalizar os dados das atividades
        if (response?.ultimas_atividades) {
          response.ultimas_atividades = response.ultimas_atividades.map((atividade: any) => ({
            ...atividade,
            // Converter data do objeto para string
            data: typeof atividade.data === 'object' && atividade.data?.date
              ? atividade.data.date
              : atividade.data,
            // Garantir que o campo 'lida' seja false quando não definido
            lida: atividade.lida ?? false,
            // Remover HTML da descrição
            descricao: this.stripHtml(atividade.descricao),
          }))
        }

        this.resumoUsuario = response

        console.log('[AtividadesStore] carregarResumo - total_nao_lidas do backend:', response.total_nao_lidas)

        // Manter compatibilidade com o sistema legado (criar cópia independente)
        this.resumo = {
          total: response.total,
          periodo: response.periodo,
          ultimas_atividades: response.ultimas_atividades ? [...response.ultimas_atividades] : [],
        }
      } catch (error) {
        console.error('Erro ao carregar resumo de atividades:', error)
        this.resumo = null
        this.resumoUsuario = null
      }
    },

    async carregarAtividadesPaginadas (pagina: number = 1, limite: number = 10, reload: boolean = false) {
      try {
        if (reload || pagina === 1) {
          this.loadingPaginadas = true
          this.atividadesPaginadas = null
        }

        const apiStore = useApiStore()
        const response = await apiStore.request(`/gerencia/atividades-usuario/paginado?pagina=${pagina}&limite=${limite}&periodo=${this.filtros.periodo}`) as AtividadesPaginadas

        // Normalizar os dados das atividades
        if (response?.data) {
          response.data = response.data.map(atividade => ({
            ...atividade,
            // Converter data do objeto para string
            data: typeof atividade.data === 'object' && atividade.data?.date
              ? atividade.data.date
              : atividade.data,
            // Garantir que o campo 'lida' seja boolean
            lida: atividade.lida ?? false,
            // Remover HTML da descrição
            descricao: this.stripHtml(atividade.descricao),
          }))
        }

        if (pagina === 1 || reload) {
          this.atividadesPaginadas = response
        } else if (this.atividadesPaginadas) {
          // Append para infinite scroll
          this.atividadesPaginadas.data = [...this.atividadesPaginadas.data, ...response.data]
          this.atividadesPaginadas.pagination = response.pagination
        } else {
          this.atividadesPaginadas = response
        }

        this.paginaAtual = pagina
      } catch (error) {
        console.error('Erro ao carregar atividades paginadas:', error)
        if (pagina === 1 || reload) {
          this.atividadesPaginadas = null
        }
      } finally {
        this.loadingPaginadas = false
      }
    },

    async carregarMaisAtividades () {
      if (!this.hasMorePages || this.loadingPaginadas) {
        return
      }

      const proximaPagina = this.paginaAtual + 1
      await this.carregarAtividadesPaginadas(proximaPagina, 10, false)
    },

    atualizarFiltros (novosFiltros: Partial<typeof this.filtros>) {
      this.filtros = { ...this.filtros, ...novosFiltros }
    },

    limparFiltros () {
      this.filtros.tipo = ''
      this.filtros.busca = ''
    },

    formatarTempo (data: string): string {
      try {
        const agora = new Date()
        const dataAtividade = new Date(data)

        if (isNaN(dataAtividade.getTime())) {
          return 'agora'
        }

        const diffMs = agora.getTime() - dataAtividade.getTime()
        const diffMinutos = Math.floor(diffMs / (1000 * 60))
        const diffHoras = Math.floor(diffMs / (1000 * 60 * 60))

        if (diffMinutos < 1) {
          return 'agora'
        }
        if (diffMinutos < 60) {
          return `${diffMinutos}m`
        }
        if (diffHoras < 24) {
          return `${diffHoras}h`
        }

        const diffDias = Math.floor(diffHoras / 24)
        if (diffDias < 30) {
          return `${diffDias}d`
        }

        const diffMeses = Math.floor(diffDias / 30)
        return `${diffMeses}mo`
      } catch (error) {
        console.error('Erro ao formatar tempo:', data, error)
        return 'agora'
      }
    },

    formatarDataCompleta (data: string): string {
      try {
        if (!data) {
          return 'Data não informada'
        }

        // Handle different date formats
        let date: Date

        // Try parsing as-is first
        date = new Date(data)

        // If invalid, try with 'T' replaced by space (Laravel format)
        if (isNaN(date.getTime()) && data.includes('T')) {
          date = new Date(data.replace('T', ' '))
        }

        // If still invalid, try manual parsing for YYYY-MM-DD HH:mm:ss format
        if (isNaN(date.getTime())) {
          const match = data.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/)
          if (match) {
            date = new Date(
              parseInt(match[1]),
              parseInt(match[2]) - 1, // months are 0-indexed
              parseInt(match[3]),
              parseInt(match[4]),
              parseInt(match[5]),
              parseInt(match[6])
            )
          }
        }

        if (isNaN(date.getTime())) {
          console.warn('Could not parse date:', data)
          return 'Data inválida'
        }

        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (error) {
        console.error('Erro ao formatar data:', data, error)
        return 'Data inválida'
      }
    },

    getIconForActivity (atividade: Atividade): string[] {
      const iconMap: Record<string, string[]> = {
        bpo_receita: ['fas', 'dollar-sign'],
        bpo_despesa: ['fas', 'credit-card'],
        receita_gise_log: ['fas', 'file-import'],
      }

      // Ícones específicos baseados na ação
      if (atividade.acao?.includes('arquivo')) {
        return ['fas', 'paperclip']
      }
      if (atividade.acao?.includes('Criou')) {
        return ['fas', 'plus']
      }
      if (atividade.acao?.includes('Editou')) {
        return ['fas', 'edit']
      }
      if (atividade.acao?.includes('Excluiu')) {
        return ['fas', 'trash']
      }

      return iconMap[atividade.tipo] || ['fas', 'info']
    },

    getIconColorClass (tipo: string, acao?: string): string {
      // Para exclusões, sempre usar cor de perigo
      if (acao === 'Excluiu') {
        return 'text-danger'
      }

      const colorMap: Record<string, string> = {
        bpo_receita: 'text-success',
        bpo_despesa: 'text-warning',
        receita_gise_log: 'text-info',
      }

      return colorMap[tipo] || 'text-secondary'
    },

    getBadgeColor (tipo: string, acao?: string): string {
      // Para exclusões, sempre usar cor de perigo
      if (acao === 'Excluiu') {
        return 'danger'
      }

      const colorMap: Record<string, string> = {
        bpo_receita: 'success',
        bpo_despesa: 'warning',
        receita_gise_log: 'info',
      }

      return colorMap[tipo] || 'secondary'
    },

    getTipoLabel (tipo: string): string {
      const labelMap: Record<string, string> = {
        bpo_receita: 'BPO Receita',
        bpo_despesa: 'BPO Despesa',
        receita_gise_log: 'Importação GISE',
      }

      return labelMap[tipo] || tipo
    },

    getMarkerClass (tipo: string, acao?: string): string {
      // Para exclusões, sempre usar cor de perigo
      if (acao === 'Excluiu') {
        return 'marker-danger'
      }

      const classMap: Record<string, string> = {
        bpo_receita: 'marker-success',
        bpo_despesa: 'marker-warning',
        receita_gise_log: 'marker-info',
      }

      return classMap[tipo] || 'marker-secondary'
    },

    async marcarComoLida (atividadeId: string) {
      try {
        const apiStore = useApiStore()
        await apiStore.request(`/gerencia/atividades-usuario/${atividadeId}/lida`, {
          method: 'POST',
        })

        // Atualizar o estado local
        this.atualizarEstadoLocalLida(atividadeId, true)
      } catch (error) {
        console.error('Erro ao marcar atividade como lida:', error)
        throw error
      }
    },

    async marcarComoNaoLida (atividadeId: string) {
      try {
        const apiStore = useApiStore()
        await apiStore.request(`/gerencia/atividades-usuario/${atividadeId}/nao-lida`, {
          method: 'POST',
        })

        // Atualizar o estado local
        this.atualizarEstadoLocalLida(atividadeId, false)
      } catch (error) {
        console.error('Erro ao marcar atividade como não lida:', error)
        throw error
      }
    },

    async marcarTodasComoLidas () {
      try {
        const apiStore = useApiStore()

        console.log('[AtividadesStore] marcarTodasComoLidas - Antes de marcar')
        console.log('[AtividadesStore] Atividades não lidas antes:',
          this.resumoUsuario?.ultimas_atividades?.filter(a => !a.lida).map(a => ({ id: a.id, lida: a.lida }))
        )

        // Usar a nova API que marca todas de uma vez
        const response = await apiStore.request('/gerencia/atividades-usuario/marcar-todas-lidas', {
          method: 'POST',
        })

        console.log('[AtividadesStore] marcarTodasComoLidas - Resposta do backend:', response)

        // Atualizar o estado local imediatamente para feedback visual
        if (this.resumoUsuario?.ultimas_atividades) {
          this.resumoUsuario.ultimas_atividades.forEach((atividade) => {
            atividade.lida = true
          })
          // Zerar contador de não lidas
          this.resumoUsuario.total_nao_lidas = 0
        }

        // Aguardar um pouco antes de recarregar para dar tempo do backend processar
        await new Promise(resolve => setTimeout(resolve, 500))

        // Recarregar o resumo do servidor para garantir sincronização
        await this.carregarResumo()

        console.log('[AtividadesStore] Atividades não lidas depois:',
          this.resumoUsuario?.ultimas_atividades?.filter(a => !a.lida).map(a => ({ id: a.id, lida: a.lida }))
        )

        // Atualizar atividades paginadas
        if (this.atividadesPaginadas?.data) {
          this.atividadesPaginadas.data.forEach((atividade) => {
            atividade.lida = true
          })
        }

        this.atividades.forEach((atividade) => {
          atividade.lida = true
        })

        // Recriar resumo legado apenas com os dados atuais do usuário
        if (this.resumoUsuario) {
          this.resumo = {
            total: this.resumoUsuario.total,
            periodo: this.resumoUsuario.periodo,
            ultimas_atividades: [...this.resumoUsuario.ultimas_atividades], // Criar nova cópia
          }
        }
      } catch (error) {
        console.error('Erro ao marcar todas as atividades como lidas:', error)
        throw error
      }
    },

    // Método auxiliar para atualizar o estado local
    atualizarEstadoLocalLida (atividadeId: string, lida: boolean) {
      console.log('[AtividadesStore] atualizarEstadoLocalLida - antes:', {
        atividadeId,
        lida,
        total_nao_lidas: this.resumoUsuario?.total_nao_lidas,
      })

      // Atualizar resumo do usuário
      if (this.resumoUsuario?.ultimas_atividades) {
        const atividade = this.resumoUsuario.ultimas_atividades.find(a => a.id === atividadeId)
        if (atividade) {
          atividade.lida = lida
          // Atualizar contador
          if (lida) {
            this.resumoUsuario.total_nao_lidas = Math.max(0, this.resumoUsuario.total_nao_lidas - 1)
          } else {
            this.resumoUsuario.total_nao_lidas += 1
          }

          console.log('[AtividadesStore] atualizarEstadoLocalLida - depois:', {
            total_nao_lidas: this.resumoUsuario.total_nao_lidas,
          })
        }
      }

      // Atualizar atividades paginadas
      if (this.atividadesPaginadas?.data) {
        const atividade = this.atividadesPaginadas.data.find(a => a.id === atividadeId)
        if (atividade) {
          atividade.lida = lida
        }
      }

      // Atualizar lista completa
      const atividadeCompleta = this.atividades.find(a => a.id === atividadeId)
      if (atividadeCompleta) {
        atividadeCompleta.lida = lida
      }

      // Recriar resumo legado apenas com os dados atuais do usuário (sem shared state)
      if (this.resumoUsuario) {
        this.resumo = {
          total: this.resumoUsuario.total,
          periodo: this.resumoUsuario.periodo,
          ultimas_atividades: [...this.resumoUsuario.ultimas_atividades], // Criar nova cópia
        }
      }
    },

  },
})
